import { ListPage, RefreshButton, StatCard } from '@skylabs-digital/backoffice-kit';
import { Copy, Files, HardDrive } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type DuplicatesPage as DuplicatesResult, useStorageActions } from '../api/storage.api';
import { formatBytes } from '../components/apps/AppList';
import { AppFilter } from '../components/shared/AppFilter';
import { Pager } from '../components/shared/Pager';
import { DuplicatesTable } from '../components/storage/DuplicatesTable';

const LIMIT = 25;

export function DuplicatesPage() {
  const api = useStorageActions();
  const [params, setParams] = useSearchParams();
  const appId = params.get('app') ?? undefined;
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<DuplicatesResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setResult(await api.duplicates({ appId, page, limit: LIMIT }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load duplicates'));
    } finally {
      setRefreshing(false);
    }
  }, [api, appId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = result?.summary;
  return (
    <ListPage
      title="Duplicates"
      description="The same content (sha256) uploaded more than once within an app and tenant. Clients can avoid it with GET /files/by-hash before uploading."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <AppFilter
            value={appId}
            onChange={(v) => {
              const next = new URLSearchParams(params);
              if (v) next.set('app', v);
              else next.delete('app');
              setParams(next, { replace: true });
              setPage(1);
            }}
          />
          <RefreshButton onRefresh={load} isRefreshing={refreshing} label={null} />
        </div>
      }
    >
      <div className="space-y-6">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Duplicate groups"
            value={summary?.groups ?? '—'}
            icon={<Copy />}
            loading={!summary}
          />
          <StatCard
            label="Files involved"
            value={summary?.files ?? '—'}
            icon={<Files />}
            loading={!summary}
          />
          <StatCard
            label="Recoverable"
            value={formatBytes(summary?.recoverableBytes)}
            hint="keeping one copy per group"
            icon={<HardDrive />}
            tone={summary && Number(summary.recoverableBytes) > 0 ? 'warning' : 'success'}
            loading={!summary}
          />
        </div>
        <DuplicatesTable rows={result?.data ?? []} loading={result === null} error={error} />
        <Pager meta={result?.meta} page={page} onPage={setPage} />
      </div>
    </ListPage>
  );
}
